import { supabase } from '../lib/supabaseClient';
import { extractDocumentText } from './extractDocumentText';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Shared CV skill extraction helpers.
 *
 * Used by the public job application, candidate onboarding and My Details so all
 * three behave identically. Extraction runs in the browser; the backend only scans
 * the resulting text against the skills catalogue and returns matches.
 */

/**
 * Extract text from a CV file and scan it for known skills.
 *
 * Already-held skills are filtered out server-side from the caller's session, so a
 * candidate is never asked to re-confirm something already on their profile.
 *
 * @param {File} file
 * @param {object} [options]
 * @param {'public_application'|'onboarding'|'profile'} [options.context]
 * @returns {Promise<{matched: Array, excludedCount: number, totalFound: number}>}
 */
export async function scanCvFileForSkills(file, options = {}) {
  const { context = 'public_application' } = options;

  const text = await extractDocumentText(file);
  if (!text || text.trim().length < 30) {
    return { matched: [], excludedCount: 0, totalFound: 0 };
  }

  const headers = { 'Content-Type': 'application/json' };

  // Send the token when we have one so the backend can exclude skills the
  // candidate already has. Anonymous callers simply get everything found.
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(`${API_URL}/cv-skills/scan`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text, context })
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to scan CV for skills');
  }

  return {
    matched: data.matched || [],
    excludedCount: data.excludedCount || 0,
    totalFound: data.totalFound || 0
  };
}

/**
 * Persist candidate-confirmed skills for an authenticated candidate.
 *
 * Inserts directly rather than using the add_candidate_skill RPC: that RPC requires
 * a non-null proficiency level and raises on duplicates, neither of which suits
 * CV-extracted skills (which have no inferable level).
 *
 * @param {string} candidateId
 * @param {Array<{skill_id: string}>} skills
 * @returns {Promise<number>} number of rows attempted
 */
export async function saveConfirmedSkills(candidateId, skills) {
  if (!candidateId || !skills?.length) return 0;

  const rows = skills.map(s => ({
    candidate_id: candidateId,
    skill_id: s.skill_id,
    level: null,
    source: 'cv_extract',
    confirmed_at: new Date().toISOString()
  }));

  const { error } = await supabase
    .from('candidate_skills')
    .upsert(rows, { onConflict: 'candidate_id,skill_id', ignoreDuplicates: true });

  if (error) throw error;
  return rows.length;
}
